import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import Logo from "@/components/Logo";
import { EASE } from "@/components/primitives";

const LINKS = [
  { label: "Projects", to: "/projects" },
  { label: "Services", to: "/services" },
  { label: "Our Approach", to: "/approach" },
  { label: "About Us", to: "/about" },
  { label: "Careers", to: "/careers" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const navigate = useNavigate();

  return (
    <header
      className="fixed inset-x-0 top-0 z-50 border-b border-ink/10 bg-paper/95 backdrop-blur-md"
      data-testid="navbar"
    >
      <div className="mx-auto flex max-w-[1440px] items-center justify-between px-6 py-2 lg:px-12">
        <button onClick={() => navigate("/")} data-testid="nav-logo-button" aria-label="ARA Turnkey Projects — home">
          <Logo />
        </button>

        <nav className="hidden items-center gap-9 lg:flex">
          {LINKS.map((l) => {
            const active = pathname === l.to;
            return (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.to.slice(1)}`}
                className={`group relative text-[12px] font-medium uppercase tracking-[0.2em] transition-colors duration-300 ${
                  active ? "text-ink" : "text-ink/70 hover:text-ink"
                }`}
              >
                {l.label}
                <span
                  className={`absolute -bottom-1 left-0 h-px bg-maroon transition-all duration-300 ${
                    active ? "w-full" : "w-0 group-hover:w-full"
                  }`}
                />
              </Link>
            );
          })}
          <button
            onClick={() => navigate("/contact")}
            data-testid="nav-cta-button"
            className="rounded-full bg-maroon px-6 py-2.5 text-[12px] font-medium uppercase tracking-[0.2em] text-paper transition-transform duration-300 hover:scale-[1.04]"
          >
            Contact Us
          </button>
        </nav>

        <button
          className="flex h-10 w-10 flex-col items-center justify-center gap-1.5 lg:hidden"
          onClick={() => setOpen(!open)}
          data-testid="nav-mobile-toggle"
          aria-label="Toggle menu"
        >
          <span className={`h-px w-6 bg-ink transition-transform duration-300 ${open ? "translate-y-[3.5px] rotate-45" : ""}`} />
          <span className={`h-px w-6 bg-ink transition-transform duration-300 ${open ? "-translate-y-[3.5px] -rotate-45" : ""}`} />
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.35, ease: EASE }}
            className="flex flex-col gap-2 bg-ink px-6 pb-10 pt-4 lg:hidden"
            data-testid="nav-mobile-menu"
          >
            {[{ label: "Home", to: "/" }, ...LINKS].map((l, i) => (
              <button
                key={l.to}
                onClick={() => { setOpen(false); navigate(l.to); }}
                data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="border-b border-paper/10 py-4 text-left font-serif text-3xl text-paper"
              >
                <span className="mr-4 text-xs font-sans" style={{ color: "#C8554F" }}>0{i + 1}</span>
                {l.label}
              </button>
            ))}
            <button
              onClick={() => { setOpen(false); navigate("/contact"); }}
              data-testid="nav-mobile-cta"
              className="mt-6 rounded-full bg-maroon px-6 py-4 text-center text-xs font-medium uppercase tracking-[0.25em] text-paper"
            >
              Contact Us
            </button>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
